/**
 * Tidy layout — the lane-aware auto-arrange behind the toolbar "Tidy" action (OPSFL-CANVAS-1).
 *
 * The lifecycle lanes are SEMANTIC: a node's lane is its `stage`, and no layout may move a node
 * out of it (the lane IS meaning — workflow-designer.md §4.2). Within that constraint the base
 * `layoutFlow` pass already gives every node a deterministic slot: rank (longest-path over control
 * edges) → row, lane + declaration order → sub-column. What it does NOT do is order siblings to
 * minimise edge crossings — hand-authored flows routinely end up with braided edges.
 *
 * This module adds the ordering pass: a hand-rolled barycenter (median-of-neighbours) sweep in the
 * Sugiyama style, permuting nodes WITHIN their lane+rank slot groups only. A generic layered
 * library (dagre/elk) was deliberately not used — both fight the fixed-lane x-constraint (they own
 * x placement) and would be a dependency spent on the one part of the problem `layoutFlow` has
 * already solved. Zero dependencies, fully deterministic: fixed sweep count, ties broken by node id.
 *
 * Output is an explicit position for EVERY node (consumed as the ephemeral drag-position overlay —
 * positions are canvas state, never part of the IR / undo stack, spec §4.6). Same definition in →
 * byte-identical positions out.
 */
import { layoutFlow, LAYOUT_CONST } from '@/flow-ir/layout';
import { isControlEdge, type FlowDefinition } from '@/flow-ir/types';
import type { XY } from '@/store/designerSlice';

/** Down-up sweep rounds. 2 rounds (4 sweeps) is the classic knee of the quality curve. */
export const TIDY_SWEEP_ROUNDS = 2;

interface SlotNode {
  id: string;
  /** Assigned slot centre-x (mutated as sweeps reorder the group). */
  cx: number;
  y: number;
}

interface Group {
  rank: number;
  /** The slot centre-x values this lane+rank group owns (fixed; membership permutes). */
  slots: number[];
  members: SlotNode[];
}

/**
 * Compute tidy positions for every node of `def`: lane-preserving, rank-banded top-to-bottom,
 * siblings ordered by neighbour barycenter to minimise control-edge crossings.
 */
export function tidyLayout(def: FlowDefinition): Record<string, XY> {
  const base = layoutFlow(def);
  if (base.nodes.length === 0) return {};

  // ---- group the base slots by lane+rank --------------------------------------------------------
  const groups = new Map<string, Group>();
  const byId = new Map<string, SlotNode>();
  for (const ln of base.nodes) {
    const key = `${ln.node.stage}|${ln.rank}`;
    const g = groups.get(key) ?? { rank: ln.rank, slots: [], members: [] };
    const sn: SlotNode = { id: ln.node.id, cx: ln.x + LAYOUT_CONST.NODE_W / 2, y: ln.y };
    g.slots.push(sn.cx);
    g.members.push(sn);
    groups.set(key, g);
    byId.set(sn.id, sn);
  }
  for (const g of groups.values()) g.slots.sort((a, b) => a - b);

  // ---- control-edge neighbour maps (data edges never gate → never influence order) --------------
  const preds = new Map<string, string[]>();
  const succs = new Map<string, string[]>();
  const push = (m: Map<string, string[]>, k: string, v: string) => {
    const arr = m.get(k);
    if (arr) arr.push(v);
    else m.set(k, [v]);
  };
  for (const e of def.edges ?? []) {
    if (!isControlEdge(e) || !byId.has(e.from) || !byId.has(e.to)) continue;
    push(preds, e.to, e.from);
    push(succs, e.from, e.to);
  }

  const ranksAsc = [...new Set([...groups.values()].map((g) => g.rank))].sort((a, b) => a - b);

  /** Reorder one group by the mean centre-x of each member's neighbours (fallback: keep slot). */
  const reorder = (g: Group, neighbours: Map<string, string[]>) => {
    const keyed = g.members.map((m) => {
      const ns = neighbours.get(m.id) ?? [];
      const xs = ns.map((n) => byId.get(n)!.cx);
      const key = xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : m.cx;
      return { m, key };
    });
    keyed.sort((a, b) => a.key - b.key || (a.m.id < b.m.id ? -1 : a.m.id > b.m.id ? 1 : 0));
    keyed.forEach(({ m }, i) => {
      m.cx = g.slots[i];
    });
  };

  for (let round = 0; round < TIDY_SWEEP_ROUNDS; round++) {
    // Downward: order each rank under its predecessors.
    for (const r of ranksAsc) {
      for (const g of groups.values()) if (g.rank === r) reorder(g, preds);
    }
    // Upward: order each rank over its successors.
    for (let i = ranksAsc.length - 1; i >= 0; i--) {
      for (const g of groups.values()) if (g.rank === ranksAsc[i]) reorder(g, succs);
    }
  }

  const out: Record<string, XY> = {};
  for (const sn of byId.values()) out[sn.id] = { x: sn.cx - LAYOUT_CONST.NODE_W / 2, y: sn.y };
  return out;
}

/**
 * Count control-edge crossings of a positioned graph (test/metric helper). Two edges cross when
 * their endpoints interleave horizontally across the same rank interval — the standard layered
 * approximation using endpoint order.
 */
export function countCrossings(def: FlowDefinition, positions: Record<string, XY>): number {
  const edges = (def.edges ?? []).filter(
    (e) => isControlEdge(e) && positions[e.from] && positions[e.to],
  );
  let crossings = 0;
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const a = edges[i];
      const b = edges[j];
      const df = positions[a.from].x - positions[b.from].x;
      const dt = positions[a.to].x - positions[b.to].x;
      if (df * dt < 0) crossings++;
    }
  }
  return crossings;
}
